/**
 * This module manages the events model of the API
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 * @author DJ Kim <dj@thebrandingfarm.com>
 */ 
import debounce from 'lodash-es/debounce'
import isEmpty from 'lodash-es/isEmpty'
import cloneDeep from 'lodash-es/cloneDeep'

import api from '@/api'
// import router from '@/router'
import { 
    // MEMOS
    VUEX_EVENTS_GET_REQUEST, 
    VUEX_EVENTS_GET_SUCCESS,
    VUEX_EVENTS_GET_FAILURE, 
    VUEX_EVENTS_APPLY_FILTERS,
    VUEX_EVENTS_CLEAR_FILTERS,
    VUEX_EVENTS_APPLY_STATUS_FILTER,
    VUEX_EVENTS_APPLY_PROGRESS_FILTER,
    VUEX_EVENTS_APPLY_TYPE_FILTER,
    VUEX_EVENTS_APPLY_DEPARTMENT_FILTER,
    VUEX_EVENTS_APPLY_CREATIVE_ROUND_FILTER,
    VUEX_EVENTS_SYNC_PAGINATION,
    VUEX_EVENTS_SET_APPROVED,
    
    // MEMO
    VUEX_EVENT_GET_REQUEST, 
    VUEX_EVENT_GET_SUCCESS, 
    VUEX_EVENT_GET_EVENTS_SUCCESS,
    VUEX_EVENT_GET_FILES_SUCCESS,
    VUEX_EVENT_UPDATE_PROP,
    VUEX_EVENT_UPDATE_APPLY_TEMPLATE,
    VUEX_EVENT_UPDATE_REQUEST,
    VUEX_EVENT_UPDATE_REQUEST_SUCCESS,
    VUEX_EVENT_UPDATE_REQUEST_FAILURE,
    
    // CREATE MEMO
    VUEX_EVENT_CREATE_INIT, 
    VUEX_EVENT_CREATE, 
    VUEX_EVENT_CREATE_APPLY_TEMPLATE,
    VUEX_EVENT_CREATE_SUCCESS,
    VUEX_EVENT_CREATE_FAILURE,
    VUEX_EVENT_CREATE_UPDATE, 
    VUEX_EVENT_CREATE_UPDATE_SUCCESS,
    VUEX_EVENT_CREATE_UPDATE_FAILURE,
    VUEX_EVENT_CREATE_CANCEL,
    VUEX_EVENT_CREATE_CLEAR_PROPS_TO_UPDATE,
    VUEX_EVENT_CREATE_UPDATE_PROP,
    VUEX_EVENT_CREATE_HAS_CHANGED,

    VUEX_EVENT_REMOVE,
    VUEX_EVENT_REMOVE_SUCCESS,
    VUEX_EVENT_REMOVE_FAILURE } from '@/store/constants/events'

import { 
    VUEX_ROUTING_EXIT_EVENT } from '@/store/constants/routing'
    
import { VUEX_AUTH_LOGOUT } from '@/store/constants/auth'

import {
    VUEX_SNACKBAR_SHOW } from '@/store/constants/snackbar'

import {
    VUEX_PROPERTIES_GET_BLACKOUT_DATES } from '@/store/constants/config/properties'
/**
 * An empty representation of a memo object model needed for new memo creation
 * @const
 * @todo This will become dynamically registered vuex module when the creation route loads
 */

let currentDate = (pos) => {

    let tzoneOffset = (new Date()).getTimezoneOffset() * 60000
    let date = new Date(Date.now() - tzoneOffset).toISOString()

    let hour = pos === 'start' ? +date.slice(11, 13) + 1 : +date.slice(11, 13) + 2

    return date.slice(0, 10) + ' ' + hour + ':00:00'  
}

let getDateStart = () => {
    return currentDate()
}

let getDateEnd = () => {
    return currentDate()
}


const createEvent_Template = {
    name:              null,
    all_day:           true,
    date_start:        currentDate('start'),
    date_end:          currentDate('end'),
    notify_users:      false,
    is_job_skip_date:  false
}

const updateEvent_Template = {
    name:              null,
    all_day:           false,
    date_start:        null,
    date_end:          null,
    notify_users:      false,
    is_job_skip_date:  false
}


/**
* Memos module state
* @const
*/
const state = {
    events: [],
    eventsLinks: {},
    eventsLoaded: true,
    eventsApproved: true,
    
    organization_event: {},
    eventLinks: {},
    eventEvents: {},
    eventEventsLinks: {},
    eventFiles: {},
    eventLoaded: false,
    restorePreviousEventList: false,
    appliedFilters: [],
    // Vuetify 2 shape: itemsPerPage/sortBy[]/sortDesc[] — matches what
    // v-data-table emits/consumes via :options.sync. v1's rowsPerPage
    // and descending caused .findIndex() to throw against a string
    // sortBy, which killed the thead render and the footer's page math.
    pagination: {
        itemsPerPage: 10,
        sortBy: ['created_at'],
        sortDesc: [true],
        page: 1,
        totalItems: 0,
    },
    
    updateEvent_Data: {},
    createEvent_Data: {},
    createEvent_PropsToUpdate: {},
    createEvent_Saved: false,
    createEvent_IsSaving: false,
    isEdit: false,
}



/**
* Memos module getters
* @const
*/
const getters = {
    /**
    * This getter returns the events attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getEventEvents: state => {
        if (state.eventLoaded)
            return state.organization_event.events
        return []
    },


    /**
    * This getter returns the files attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getEventFiles: state => {
        if (state.eventLoaded)
            return state.eventFiles
        return []
    },


    /**
    * This getter returns the team members attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getEventTeam: state => {
        if (state.eventLoaded)
            return state.organization_event.team
        return []
    },


    /**
    * This getter returns the swaps attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getEventSwapRows: state => {
        if (state.eventLoaded)
            return state.organization_event.swapRows
        return []
    },


    /**
    * This getter returns the swaps attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getEventId: state => {
        if (state.eventLoaded)
            return state.organization_event.id
        return null
    },


    /**
    * This getter returns the swaps attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getCreateEventData: state => {
        return state.createEvent_Data
    }
}




/**
 * Events module action definitions
 * @const
 */
const actions = {
    /**
    * Get all events
    * Params :
        approved: (approved events | event requests)
        ...table pagination (sorting, page, itemsPerRow, etc)
        filters
    * @function
    */
    /* TODO: needs error handling */
    [VUEX_EVENTS_GET_REQUEST]: async ({ state, commit }) => {
        commit(VUEX_EVENTS_GET_REQUEST)

        const sortKey = (state.pagination.sortBy && state.pagination.sortBy[0]) || 'created_at'
        const sortDesc = !!(state.pagination.sortDesc && state.pagination.sortDesc[0])
        let params = Object.assign(
            {
                approved: state.eventsApproved,
                page: state.pagination.page,
                per_page: state.pagination.itemsPerPage,
                sort: sortDesc ? '-' + sortKey : sortKey,
            },
        )

        for (var f in state.appliedFilters) {
            var key = state.appliedFilters[f]['filterOn']
            var val = state.appliedFilters[f]['id']
            if (!params.hasOwnProperty(key)) {
                params[key] = val
            } else {
                params[key] = params[key] + ',' + val
            }
        }

        await api.get('organization_events', { params }).then(async (response) => {
            await commit(VUEX_EVENTS_GET_SUCCESS, response.data)
        }).catch((err) => {
            commit(VUEX_EVENTS_GET_FAILURE, err)
        })  
    },

    [VUEX_EVENTS_CLEAR_FILTERS]: async ({ commit }) => {
        await commit(VUEX_EVENTS_CLEAR_FILTERS)
    },

    [VUEX_EVENTS_APPLY_FILTERS]: async ({ commit, dispatch }, filterItem) => {
        await commit(VUEX_EVENTS_APPLY_FILTERS, filterItem)
        await dispatch(VUEX_EVENTS_GET_REQUEST)
    },

    [VUEX_EVENTS_APPLY_STATUS_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'status'
        await dispatch(VUEX_EVENTS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_EVENTS_APPLY_PROGRESS_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'progress'
        await dispatch(VUEX_EVENTS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_EVENTS_APPLY_TYPE_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'type'
        await dispatch(VUEX_EVENTS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_EVENTS_APPLY_DEPARTMENT_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'department'
        await dispatch(VUEX_EVENTS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_EVENTS_APPLY_CREATIVE_ROUND_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'cretive_round'
        await dispatch(VUEX_EVENTS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_EVENTS_SYNC_PAGINATION]: async({commit, dispatch, state}, pagination) => {
        if (state.eventsLoaded) {
            await commit(VUEX_EVENTS_SYNC_PAGINATION, pagination)
            await dispatch(VUEX_EVENTS_GET_REQUEST)
        }
    },





    /**
    * Get event -> get events -> get files
    * Params :
        event id
    * @function
    */
    /* TODO: needs error handling */
    [VUEX_EVENT_GET_REQUEST]:  ({ dispatch, commit }, id) => {
        commit(VUEX_EVENT_GET_REQUEST);


        api.get(`organization_events/${id}`).then((response) => {
            commit(VUEX_EVENT_GET_SUCCESS, response.data)
        })
    },

    /**
    * Update the current event's properties
    * @function
    */
    [VUEX_EVENT_UPDATE_REQUEST]: async ({ state, dispatch, commit }, payload) => {
        await api.patch(`/organization_events/${state.organization_event.id}`, payload).then((response) => {
            commit(VUEX_EVENT_UPDATE_REQUEST_SUCCESS, response)
            dispatch(VUEX_EVENT_GET_REQUEST, state.organization_event.id)
            dispatch(VUEX_EVENTS_GET_REQUEST)
            commit(VUEX_EVENT_CREATE_CANCEL)

            //Show snackbar for success
            dispatch(VUEX_SNACKBAR_SHOW, {
                description_text: "Success! Your event has been saved.",
                button_text: "",
                timeout: 4000,
                button_dispatch: "",
                button_dispatch_payload: {},
            })
        }).catch((err) => {
            commit(VUEX_EVENT_UPDATE_REQUEST_FAILURE, err)
        })
    },

    /**
     * Update the current event's name
     * @function
     */
    [VUEX_EVENT_UPDATE_PROP]: async ({dispatch}, params) => {
        await dispatch(VUEX_EVENT_UPDATE_REQUEST, params)
    },


    /**
     * Create event Request
     * @function
     */
    [VUEX_EVENT_CREATE_INIT]: ({ state, commit, dispatch }, opts) => {
        commit(VUEX_EVENT_CREATE_APPLY_TEMPLATE, opts)
        commit(VUEX_EVENT_CREATE_INIT)

        //GLOBAL ACTION PROMPT: Pass array of optional states to watch
        var stateChangeWatchers = [
            ['events','createEvent_Data']
        ]
        dispatch('VUEX_GLOBAL_ACTION_PROMPT_SET_STATE_CHANGE_WATCHERS', { stateChangeWatchers: stateChangeWatchers})
    },

    [VUEX_EVENT_CREATE]: debounce(async ({ state, commit, dispatch }, payload) => {
        commit(VUEX_EVENT_CREATE)

        await api.post(`/organization_events`, payload).then((response) => {
            commit(VUEX_EVENT_CREATE_SUCCESS, response.data.data)
            commit(VUEX_EVENT_CREATE_CLEAR_PROPS_TO_UPDATE)
            dispatch(VUEX_EVENTS_GET_REQUEST)
            commit(VUEX_EVENT_CREATE_CANCEL)

            //Show snackbar for success
            dispatch(VUEX_SNACKBAR_SHOW, {
                description_text: "Success! Your event has been created.",
                button_text: "",
                timeout: 4000,
                button_dispatch: "",
                button_dispatch_payload: {},
            })

            dispatch(VUEX_PROPERTIES_GET_BLACKOUT_DATES)

        }).catch((err) => {
            commit(VUEX_EVENT_CREATE_FAILURE)
            console.error(err)
        })      

        // TODO: Move this to dispatch in routing
        // TODO: Diff destination based on role
        // router.push({ name: 'events-manage' })
    }, 1200),

    [VUEX_EVENT_CREATE_UPDATE]: async ({ state, commit, dispatch }) => {
        // commit(VUEX_EVENT_CREATE_UPDATE)

        await api.patch(`/organization_events/${state.createEvent_Data.id}`, state.createEvent_Data).then((response) => {
            commit(VUEX_EVENT_CREATE_UPDATE_SUCCESS, response.data.data)
            dispatch(VUEX_EVENTS_GET_REQUEST)
            commit(VUEX_EVENT_CREATE_CLEAR_PROPS_TO_UPDATE)
        }).catch((err) => {
            commit(VUEX_EVENT_CREATE_UPDATE_FAILURE)
            console.error(err)
        })      
    },

    [VUEX_EVENT_CREATE_HAS_CHANGED]: ({state, commit}) => {
        if(!isEmpty(state.createEvent_PropsToUpdate)) {
            commit(VUEX_EVENT_CREATE_HAS_CHANGED, true)
        } else {
            commit(VUEX_EVENT_CREATE_HAS_CHANGED, false)
        }
    },

    [VUEX_EVENT_CREATE_CANCEL]: ({ state, commit, dispatch }) => {
        commit(VUEX_EVENT_CREATE_CANCEL)
    },

    [VUEX_EVENT_CREATE_UPDATE_PROP]: ({ commit, dispatch }, payload) => {
        commit(VUEX_EVENT_CREATE_UPDATE_PROP, payload)
    },

    [VUEX_EVENT_REMOVE]: async ({ state, commit, dispatch }) => {
        await commit(VUEX_EVENT_REMOVE)

        await api.delete(`/organization_events/${state.organization_event.id}`).then((response) => {
            commit(VUEX_EVENT_REMOVE_SUCCESS, response.data.data)
            dispatch(VUEX_EVENTS_GET_REQUEST)
            commit(VUEX_EVENT_CREATE_CANCEL)
        }).catch((err) => {
            commit(VUEX_EVENT_REMOVE_FAILURE)
            console.error(err)
        })  
    },
}

/**
 * events module mutation definitions
 * @const
 */
const mutations = {

    [VUEX_EVENTS_GET_REQUEST]: (state) => {
        state.events = []
        state.eventsLoaded = false
        state.pagination.totalItems = 0
    },

    [VUEX_EVENTS_GET_SUCCESS]: (state, payload) => {
        state.events = payload.data
        state.eventsLinks = payload.links
        state.eventsLoaded = true
        state.pagination.totalItems = payload.meta.total
        state.pagination.page = payload.meta.current_page
    },

    [VUEX_EVENTS_GET_FAILURE]: () => {

    },

    //
    // Filtering
    //
    [VUEX_EVENTS_APPLY_FILTERS]: (state, filterItem) => {
        let idx = state.appliedFilters.findIndex(x => x.id === filterItem.id && x.filterOn === filterItem.filterOn)
        if (idx > -1) {
            state.appliedFilters.splice(idx, 1) // remove
        } else {
            state.appliedFilters.push(filterItem) // add
        }
    },

    [VUEX_EVENTS_CLEAR_FILTERS]: (state) => {
        state.appliedFilters = []
    },


    [VUEX_EVENTS_SYNC_PAGINATION]: (state, pagination) => {
        // v-data-table emits options without totalItems; preserve it so
        // the footer's "X-Y of N" and right-arrow enable logic survive.
        state.pagination = { ...state.pagination, ...pagination }
    },

    [VUEX_EVENTS_SET_APPROVED]: (state, value) => {
        state.eventsApproved = value
    },



    
    [VUEX_EVENT_GET_REQUEST]: (state) => {
        state.organization_event = {}
        state.eventLoaded = false
    },

    [VUEX_EVENT_GET_SUCCESS]: (state, payload) => {
        state.organization_event = payload.data
        state.eventLinks = payload.links
        state.eventLoaded = true
        state.createEvent_Saved = true
    },

    [VUEX_EVENT_GET_EVENTS_SUCCESS]: (state, payload) => {
        state.eventEvents = payload.data
        state.eventEventsLinks = payload.links
    },

    [VUEX_EVENT_GET_FILES_SUCCESS]: (state, payload) => {
        state.eventFiles = payload.data
    },

    [VUEX_EVENT_UPDATE_APPLY_TEMPLATE]: (state) => {
        Object.assign(state.updateEvent_Data, updateEvent_Template)
    },

    [VUEX_EVENT_UPDATE_APPLY_TEMPLATE]: (state) => {
        state.updateEvent_Data = updateEvent_Template
    },


    [VUEX_EVENT_UPDATE_REQUEST_SUCCESS]: (rootState, payload) => {
        rootState.lastSuccess = payload
    },

    [VUEX_EVENT_UPDATE_REQUEST_FAILURE]: (rootState, error) => {
        rootState.lastError = error
    },

    [VUEX_EVENT_CREATE_APPLY_TEMPLATE]: (state, opts) => {
        state.createEvent_Data = cloneDeep(createEvent_Template)

        if (opts && opts.edit) {
            state.isEdit = true
        } else {
            state.isEdit = false
        }
    },

    [VUEX_EVENT_CREATE_INIT]: (state, payload) => {

    },

    // CREATE EVENT
    [VUEX_EVENT_CREATE]: (state) => {
        state.createEvent_IsSaving = true
    },

    [VUEX_EVENT_CREATE_SUCCESS]: (state, payload) => {
        state.createEvent_Data = payload
        state.createEvent_IsSaving = false
        state.createEvent_Saved = true
    },

    [VUEX_EVENT_CREATE_FAILURE]: (state) => {
        state.createEvent_IsSaving = false
        state.createEvent_Saved = false
        // TODO: report error
    },


    // UPDATE EVENT
    [VUEX_EVENT_CREATE_UPDATE]: (state) => {
        state.createEvent_IsSaving = true
    },

    [VUEX_EVENT_CREATE_UPDATE_SUCCESS]: (state, payload) => {
        state.createEvent_Data = payload
        state.createEvent_IsSaving = false
    },

    [VUEX_EVENT_CREATE_UPDATE_FAILURE]: (state) => {
        state.createEvent_IsSaving = false
        state.createEvent_Saved = false
        // TODO: report error
    },

    [VUEX_EVENT_CREATE_CLEAR_PROPS_TO_UPDATE]: (state) => {
        var props = Object.getOwnPropertyNames(state.createEvent_PropsToUpdate);
        for (var i = 0; i < props.length; i++) {
          delete state.createEvent_PropsToUpdate[props[i]]
        }
    },


    //  UPDATE PROP IN STORE
    [VUEX_EVENT_CREATE_UPDATE_PROP]: (state, payload) => {
                // TODO: can this be done better/somewhere else
        // switch( Object.keys(payload)[0] ) {

        //     case 'name' :
        //         delete state.createEvent_PropsToUpdate.name
        //         break

        //     case 'content' :
        //         delete state.createEvent_PropsToUpdate.content
        //         break

        //     default :
        //         break
        // }
        let mergedData = Object.assign(state.createEvent_Data, payload)
        state.createEvent_Data = cloneDeep(mergedData)

        Object.assign(state.createEvent_PropsToUpdate, payload)
    },

    [VUEX_EVENT_CREATE_HAS_CHANGED]: (state, payload) => {
        state.createevent_HasChanged = payload
    },

    // CANCEL EVENT
    [VUEX_EVENT_CREATE_CANCEL]: (state) => {
        state.createEvent_IsSaving = false
        //state.createEvent_Saved = false
    },

    [VUEX_EVENT_REMOVE]: (state, payload) => {
    },


    [VUEX_EVENT_REMOVE_SUCCESS]: (state, payload) => {
        state.createEvent_Data = payload
        state.createEvent_IsSaving = false
    },

    [VUEX_EVENT_REMOVE_FAILURE]: (state, payload) => {
        state.createEvent_IsSaving = false
        state.createEvent_Saved = false
        // TODO: report error
    },


    //
    // AUTH EVENTS
    //
    [VUEX_AUTH_LOGOUT]: (state) => {
        state.events = []
        state.eventsLoaded = false
        state.organization_event = {}
        state.eventLoaded = false
    },


    //
    // ROUTING EVENTS
    //
    [VUEX_ROUTING_EXIT_EVENT]: (state) => {
        state.restorePreviousEventList = true
        state.pagination.page = 1
        state.pagination.sortBy = ['created_at']
        state.pagination.sortDesc = [true]
    }
}

/**
 * The events module of our Vuex store.
 * @module store/modules/memos
 */
export default {
  state,
  getters,
  actions,
  mutations,
}
